import * as React from "react"
import {useState} from "react"
import {Check, LucideIcon} from "lucide-react"
import {Button} from "@/components/ui/button"
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuGroup,
    DropdownMenuItem,
    DropdownMenuLabel,
    DropdownMenuPortal,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/ui/tooltip"
import {cn} from "@/lib/utils"
import {navigateToUrl} from "@/main/common/BrowserNavigation"
import {MetaHandler} from "@/main/common/MetaHandler"
import {LocalizationUtil} from "@/main/util/LocalizationUtil"
import {
    type ContextualAccountAccessDialogContext,
    resolveContextualAccountAccessDialogOptions,
} from "@/main/react/account_access/AccountAccessDialogContext"
import {openAccountAccessDialog} from "@/main/react/account_access/AccountAccessDisplayerDialog"
import {useTouchArbitratedTrigger} from "@/main/react/hooks/useTouchArbitratedTrigger"
import {CtaButton} from "./CtaButton"
import {CtaButtonIcon} from "./CtaButtonIcon"
import {CtaSize, CtaVariant, resolveActiveClass} from "./ctaShared"

type DropdownItemBase = {
    id?: string
    icon: LucideIcon
    text?: string | null
    extraClass?: string
    href?: string
    destructive?: boolean
    /**
     * A menu item shown but not actionable (Radix `disabled`: skipped by the keyboard, no `onSelect`). For a
     * STATE the member should see in place of the verb, e.g. "Sent to subscribers" once a post has gone out.
     */
    disabled?: boolean
    /**
     * Marks the item as one option of a choose-one menu (FI-607). When DEFINED, the row is announced as a
     * `menuitemradio` with `aria-checked`, and a `true` draws a trailing check, so the open menu states
     * which option is in force instead of leaving the trigger glyph to imply it. Leave it undefined on a
     * plain action item: `false` is a state ("not this one"), not the absence of one.
     */
    checked?: boolean
    onClick?: (ev?: Event) => void | Promise<void>
}

export type DropdownItem = DropdownItemBase & (
    | {
        requiresLogin: false
        accountAccessDialog?: never
    }
    | {
        requiresLogin: true
        accountAccessDialog: ContextualAccountAccessDialogContext
    }
)

export type DropdownSection = {
    id?: string
    title?: string | null
    items: DropdownItem[]
}

type CtaButtonDropdownBaseProps = {
    icon: LucideIcon
    text?: string | null
    /**
     * The accessible name for an icon-only trigger, forwarded to `CtaButton`. Every `…` overflow menu
     * in the product is one of these, and each one shipped announcing as a bare "button" until
     * `DASHBOARD-1` / `GROUP-6` / `DISCUSSION-13`. Use an EXISTING catalog key (AGENTS.md rule 11).
     */
    ariaLabel?: string | null
    playwrightId?: string
    extraClass?: string
    buttonData?: string | null
    displayTextOnMobile?: boolean
    dropdownItems?: DropdownItem[]
    dropdownSections?: DropdownSection[]
    /**
     * Notified whenever the menu opens or closes, including the internal closes (an item selected, or the
     * page scrolled). Opening a menu is a deliberate act, which makes it the right moment to load state
     * only the menu needs — see `useUserSafetyActions`, which fetches the viewer's block/mute relationship
     * with a comment's author on open rather than on mount, so a discussion with forty comments does not
     * issue twenty requests nobody asked for.
     */
    onOpenChange?: (open: boolean) => void
    buttonActive?: boolean
    activeClass?: string
    requiresLogin?: boolean
    disabledReason?: string | null
    loadingText?: string | null
    loadingReason?: string | null
    size?: CtaSize
    variant?: CtaVariant
    isLoading?: boolean
}

type CtaButtonDropdownLoginGateProps =
    | {
        requiresLogin?: false
        accountAccessDialog?: never
    }
    | {
        requiresLogin: true
        accountAccessDialog: ContextualAccountAccessDialogContext
    }

/** `SERIES-48`: the ghost trigger's open-state wash. Exported for the spec. */
export const CTA_DROPDOWN_GHOST_OPEN_CLASS = "data-[state=open]:bg-muted"

export type CtaButtonDropdownProps = CtaButtonDropdownBaseProps & CtaButtonDropdownLoginGateProps

export function CtaButtonDropdown({
    icon,
    text = null,
    ariaLabel = null,
    playwrightId = undefined,
    extraClass = "",
    buttonData = null,
    displayTextOnMobile = true,
    dropdownItems = [],
    dropdownSections = undefined,
    onOpenChange,
    buttonActive = false,
    activeClass,
    requiresLogin = false,
    accountAccessDialog,
    disabledReason = null,
    loadingText = null,
    loadingReason = null,
    size = "md",
    variant = "ghost",
    ...rest
    // ── `onClick` IS NOT A PROP OF THIS COMPONENT, AND THE COMPILER SAYS SO ──────────────────────
    //
    // The trigger's click belongs to `useTouchArbitratedTrigger` (below), which owns a two-event
    // contract — `pointerdown`/`pointerup` decide, the click ACTS — that a second click handler on
    // the same element cannot participate in. Until 2026-09-08 the component declared `onClick`,
    // destructured it, and then referenced it nowhere: a caller could pass one, `make build` was
    // green, and nothing happened (register F31, open since 2026-09-04).
    //
    // Swallowing it silently was the defect; accepting it would have been worse. So it is OMITTED
    // from the inherited `Button` props, which turns "passed and ignored" into a compile error at the
    // call site — the same "the compiler enumerates the exemptions" move rule 33 makes for
    // `{ replace: true }`. A caller that genuinely needs to react to the trigger has `onOpenChange`.
}: CtaButtonDropdownProps & Omit<React.ComponentProps<typeof Button>, "size" | "variant" | "onClick">) {
    const [open, setOpen] = useState(false)

    // Notify on the STATE, not on Radix's callback: the scroll-close and the item-select close below both
    // call `setOpen(false)` directly, and a subscriber wired only to Radix's own handler would still
    // believe the menu was open after the first item was picked. Reading it off `open` means every route
    // to a transition is covered, and none of them can fire during a render.
    const onOpenChangeRef = React.useRef(onOpenChange)
    onOpenChangeRef.current = onOpenChange
    const skipInitialOpenNotificationRef = React.useRef(true)
    React.useEffect(() => {
        if (skipInitialOpenNotificationRef.current) {
            skipInitialOpenNotificationRef.current = false
            return
        }
        onOpenChangeRef.current?.(open)
    }, [open])

    // While the menu is open, close it as soon as the user scrolls. The menu is
    // non-modal (see below) so the feed keeps scrolling freely; without this the
    // menu would stay anchored to its trigger and drift off-screen as the user
    // scrolls, leaving them confused about what happened. Attaching on the next
    // frame avoids the open-time focus/positioning pass self-closing the menu.
    React.useEffect(() => {
        if (!open) {
            return
        }
        let attached = false
        const closeOnScroll = () => setOpen(false)
        const raf = requestAnimationFrame(() => {
            attached = true
            window.addEventListener("scroll", closeOnScroll, { capture: true, passive: true })
        })
        return () => {
            cancelAnimationFrame(raf)
            if (attached) {
                window.removeEventListener("scroll", closeOnScroll, { capture: true } as EventListenerOptions)
            }
        }
    }, [open])

    // ── OPEN ON A TAP, NOT ON TOUCHDOWN ──────────────────────────────────────────────────────────
    //
    // Radix's trigger toggles on `pointerdown`, so on a touchscreen the menu opened the instant the
    // finger touched down and the close-on-scroll effect above then tore it away again. Every
    // discussion card carries one of these at the right edge of its action row, which on a phone is
    // exactly where a thumb starts a swipe (operator report, 2026-08-14).
    //
    // The whole gesture contract — the suppression, the pointer-sequence latch, the WebKit fallback,
    // and the two shipped defects that shaped it (2026-09-01 iOS, 2026-09-02 Chromium) — now lives in
    // ONE place, because the two handlers must agree and four hand-copied pairs cannot guarantee that
    // (F8, 2026-09-04). Read it before changing anything here:
    // `src/main/react/hooks/useTouchArbitratedTrigger.ts`.
    const triggerGestureProps = useTouchArbitratedTrigger(
        React.useCallback(() => setOpen((previous) => !previous), []),
    )

    const triggerLoginGateProps = requiresLogin
        ? { requiresLogin: true as const, accountAccessDialog: accountAccessDialog! }
        : { requiresLogin: false as const }

    const resolvedActiveClass = resolveActiveClass(buttonActive, variant, activeClass, extraClass)
    // `SERIES-48` (A50): the open trigger holds the ghost variant's own hover wash (`hover:bg-muted` in
    // `getCtaVariantClasses`), keyed on Radix's `data-state="open"` rather than on hover. A touch leaves no
    // hover, so a tapped `…` used to open its menu under a bare glyph while a clicked one showed a filled
    // pill. Ghost only: the filled variants already read as a control at rest, and `bg-muted` would
    // repaint them. Sibling precedent: `NavigationMenuDropdown`'s `DISCUSSION-109` open class.
    const triggerExtraClass = extraClass + " cta-btn-dropdown" + (variant === "ghost" ? " " + CTA_DROPDOWN_GHOST_OPEN_CLASS : "")
    const sections = (dropdownSections && dropdownSections.length)
        ? dropdownSections
        : [{ id: "default", title: null, items: dropdownItems ?? [] }]

    return (
        <DropdownMenu open={open} onOpenChange={setOpen} modal={false}>
            <DropdownMenuTrigger asChild>
                {disabledReason ? (
                    <Tooltip>
                        <TooltipTrigger asChild>
                            <CtaButton
                                icon={icon}
                                text={text}
                                ariaLabel={ariaLabel}
                                playwrightId={playwrightId}
                                extraClass={triggerExtraClass}
                                buttonData={buttonData ?? undefined}
                                buttonActive={buttonActive}
                                activeClass={activeClass}
                                disabledReason={disabledReason}
                                loadingText={loadingText}
                                loadingReason={loadingReason}
                                size={size}
                                variant={variant}
                                suppressTooltip={true}
                                displayTextOnMobile={displayTextOnMobile}
                                {...rest}
                                {...triggerGestureProps}
                                {...triggerLoginGateProps}
                            />
                        </TooltipTrigger>
                        <TooltipContent side="top">{disabledReason}</TooltipContent>
                    </Tooltip>
                ) : (
                    <CtaButton
                        icon={icon}
                        text={text}
                        ariaLabel={ariaLabel}
                        playwrightId={playwrightId}
                        extraClass={triggerExtraClass}
                        buttonData={buttonData ?? undefined}
                        buttonActive={buttonActive}
                        activeClass={activeClass}
                        loadingText={loadingText}
                        loadingReason={loadingReason}
                        size={size}
                        variant={variant}
                        suppressTooltip={true}
                        displayTextOnMobile={displayTextOnMobile}
                        // `{...rest}` FIRST, deliberately. Anything that arrives through it — from an
                        // untyped call site, or a future inherited Button prop — must not be able to
                        // overwrite the gesture contract's handlers, which is exactly what a later
                        // spread does. Pinned by CtaButtonDropdown.spec.tsx.
                        {...rest}
                        {...triggerGestureProps}
                        {...triggerLoginGateProps}
                    />
                )}
            </DropdownMenuTrigger>

            <DropdownMenuPortal>
                <DropdownMenuContent
                    side="bottom"
                    align="end"
                    className={cn(
                        // Semantic tokens only (rule 23 token table): the popover surface is
                        // `--popover`, its hairline is `--border`, and a floating layer's elevation is
                        // the `--surface-shadow-lg` TOKEN — not `bg-background1` (the PAGE background),
                        // `border-neutral` (gray-400, several steps heavier than the hairline every
                        // card on the page uses) and a literal `shadow-lg`. Overriding
                        // `DropdownMenuContent`'s own correct tokens is what painted the app's one
                        // leader menu on a different surface from every other popover.
                        "min-w-max w-min flex-col gap-2 origin-top-right rounded-md z-layer-top p-2",
                        "bg-popover text-popover-foreground border border-border shadow-[var(--surface-shadow-lg)]",
                    )}
                >
                    <DropdownMenuGroup>
                        {sections.map((section, sidx) => {
                            return (
                                <React.Fragment key={section.id ?? `section-${sidx}`}>
                                    {sidx > 0 && <DropdownMenuSeparator />}
                                    {section.title && (
                                        <DropdownMenuLabel className="px-2 py-1.5 text-xs text-muted-foreground">
                                            {section.title}
                                        </DropdownMenuLabel>
                                    )}
                                    {section.items.map((item: DropdownItem, idx: number) => {
                                        const Icon = item.icon
                                        return (
                                            <DropdownMenuItem
                                                key={item.id ?? `${item.text ?? "item"}-${idx}`}
                                                data-playwright-id={item.id ? `dropdown-item-${item.id}` : undefined}
                                                data-variant={item.destructive ? "destructive" : "default"}
                                                disabled={item.disabled}
                                                // FI-607: a choose-one option says which one is in force.
                                                // Radix spreads these after its own `role="menuitem"`.
                                                {...(item.checked !== undefined
                                                    ? {role: "menuitemradio", "aria-checked": item.checked}
                                                    : {})}
                                                className={cn("w-full px-0 py-0 standard-hover", item.extraClass)}
                                                onSelect={(e?: Event) => {
                                                    try {
                                                        e?.preventDefault()
                                                        e?.stopPropagation()
                                                    } catch {
                                                        // ignore if event shape differs
                                                    }
                                                    if (item.requiresLogin && !MetaHandler.isUserLoggedIn()) {
                                                        openAccountAccessDialog(LocalizationUtil.get, resolveContextualAccountAccessDialogOptions(LocalizationUtil.get, item.accountAccessDialog))
                                                        setOpen(false)
                                                        return
                                                    }
                                                    if (item.onClick) {
                                                        void item.onClick(e)
                                                    } else if (item.href) {
                                                        navigateToUrl(item.href, {
                                                            source: item.id ? `cta-dropdown:${item.id}` : "cta-dropdown",
                                                        })
                                                    }
                                                    setOpen(false)
                                                }}
                                            >
                                                <div
                                                    className={cn(
                                                        // `px-3 py-2 text-sm` renders exactly 36px (8 + 20 + 8), 8px under the
                                                        // rule-2 floor, on every menu in the app — this is the only menu a group
                                                        // leader reaches most actions through. The floor is gated on the INPUT
                                                        // DEVICE, never a `sm:`/`lg:` breakpoint, so a mouse keeps the dense 36px.
                                                        //
                                                        // NOT `touch-hit-44`: that centres a 44x44 expander, which on a ~174px-wide
                                                        // menu row would REPLACE a full-width hit area with a 44px one. The
                                                        // expander is for controls whose box must not grow; a menu row's may.
                                                        "flex items-center gap-2 w-full px-3 py-2 text-sm",
                                                        "touch-manipulation [@media(pointer:coarse)]:min-h-11",
                                                        item.destructive ? "text-destructive-text" : "text-foreground",
                                                    )}
                                                >
                                                    {Icon && (
                                                        <CtaButtonIcon
                                                            icon={Icon}
                                                            resolvedActiveClass={resolvedActiveClass}
                                                            // `PROFILE-62` (round 41 A32): the label's token, not the fill's.
                                                            // `--destructive` is theme-invariant (it is the FILL behind
                                                            // destructive buttons), so in dark the glyph stayed deep red
                                                            // beside a light-red label. `stroke` is what the icon paints,
                                                            // so the base item's `*:[svg]:text-destructive-text` never
                                                            // reached it.
                                                            className={item.destructive ? "stroke-destructive-text" : "stroke-foreground"}
                                                        />
                                                    )}
                                                    {item.text != null && (
                                                        <span className={cn("p-0", !displayTextOnMobile ? "hidden sm:inline" : "")}>
                                                            {item.text}
                                                        </span>
                                                    )}
                                                    {item.checked ? (
                                                        <Check
                                                            aria-hidden="true"
                                                            data-playwright-id="dropdown-item-checked"
                                                            className="ml-auto size-4 shrink-0 stroke-foreground"
                                                        />
                                                    ) : null}
                                                </div>
                                            </DropdownMenuItem>
                                        )
                                    })}
                                </React.Fragment>
                            )
                        })}
                    </DropdownMenuGroup>
                </DropdownMenuContent>
            </DropdownMenuPortal>
        </DropdownMenu>
    )
}
